/**
 * Centralized Logging Utility
 *
 * Provides module-based logging with configurable levels.
 * Levels can be changed at runtime via localStorage.
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   const log = logger('TMSLayer');
 *
 *   log.debug('Detailed info');  // Only in debug mode
 *   log.info('General info');    // debug + production
 *   log.warn('Warning');         // debug + production
 *   log.error('Error');          // Always shown
 *
 * Configure in browser console:
 *   localStorage.setItem('em3-log-TMSLayer', 'debug')
 *   localStorage.setItem('em3-log-TMSLayer', 'production')
 *   localStorage.setItem('em3-log-TMSLayer', 'off')
 *   localStorage.setItem('em3-log-all', 'debug')  // Override all modules
 */

export type LogLevel = 'debug' | 'production' | 'off';

// Default log levels per module
const DEFAULT_LEVELS: Record<string, LogLevel> = {
  // Layer system
  LayerDrawerProvider: 'production',
  LayerRegistry: 'production',
  TMSLayer: 'production',
  WMSLayer: 'production',
  WFSLayer: 'production',
  MapboxStyleLayer: 'production',
  LiveGeoJSONLayer: 'production',

  // Map components
  EasyMap: 'production',

  // Services
  LayerService: 'production',
  ThemeService: 'production',

  // Default for unregistered modules
  _default: 'production',
};

/**
 * Get the effective log level for a module
 */
function getLogLevel(module: string): LogLevel {
  // Check for global override first
  const globalOverride = localStorage.getItem('em3-log-all');
  if (globalOverride && isValidLevel(globalOverride)) {
    return globalOverride as LogLevel;
  }

  // Check for module-specific override
  const moduleOverride = localStorage.getItem(`em3-log-${module}`);
  if (moduleOverride && isValidLevel(moduleOverride)) {
    return moduleOverride as LogLevel;
  }

  // Fall back to default
  return DEFAULT_LEVELS[module] || DEFAULT_LEVELS._default;
}

function isValidLevel(level: string): boolean {
  return level === 'debug' || level === 'production' || level === 'off';
}

/**
 * Check if a log should be shown based on level
 */
function shouldLog(module: string, logType: 'debug' | 'info' | 'warn' | 'error'): boolean {
  const level = getLogLevel(module);

  if (level === 'off') {
    // Only errors shown when off
    return logType === 'error';
  }

  if (level === 'production') {
    // Errors and warnings only
    return logType === 'error' || logType === 'warn';
  }

  // Debug mode - show everything
  return true;
}

/**
 * Create a logger instance for a module
 */
export function logger(module: string) {
  const prefix = `[${module}]`;

  return {
    /**
     * Debug level - verbose logging, only shown in debug mode
     */
    debug: (...args: any[]) => {
      if (shouldLog(module, 'debug')) {
        console.log(prefix, ...args);
      }
    },

    /**
     * Info level - general information, shown in debug and production
     */
    info: (...args: any[]) => {
      if (shouldLog(module, 'info')) {
        console.log(prefix, ...args);
      }
    },

    /**
     * Warning level - shown in debug and production
     */
    warn: (...args: any[]) => {
      if (shouldLog(module, 'warn')) {
        console.warn(prefix, ...args);
      }
    },

    /**
     * Error level - always shown (except when off)
     */
    error: (...args: any[]) => {
      if (shouldLog(module, 'error')) {
        console.error(prefix, ...args);
      }
    },

    /**
     * Success indicator - shown in debug and production
     * Use for important success messages like "Layer enabled"
     */
    success: (...args: any[]) => {
      if (shouldLog(module, 'info')) {
        console.log(prefix, '✓', ...args);
      }
    },
  };
}

/**
 * Utility to set log level from console
 * Usage: window.setLogLevel('TMSLayer', 'debug')
 */
export function setLogLevel(module: string, level: LogLevel): void {
  if (!isValidLevel(level)) {
    console.error(`Invalid log level: ${level}. Use 'debug', 'production', or 'off'`);
    return;
  }
  localStorage.setItem(`em3-log-${module}`, level);
  console.log(`Log level for ${module} set to: ${level}`);
}

/**
 * Set all modules to a specific level
 */
export function setAllLogLevels(level: LogLevel): void {
  if (!isValidLevel(level)) {
    console.error(`Invalid log level: ${level}. Use 'debug', 'production', or 'off'`);
    return;
  }
  localStorage.setItem('em3-log-all', level);
  console.log(`All log levels set to: ${level}`);
}

/**
 * Clear all log level overrides (revert to defaults)
 */
export function resetLogLevels(): void {
  localStorage.removeItem('em3-log-all');
  Object.keys(DEFAULT_LEVELS).forEach(module => {
    localStorage.removeItem(`em3-log-${module}`);
  });
  console.log('All log levels reset to defaults');
}

/**
 * Show current log configuration
 */
export function showLogConfig(): void {
  console.log('=== Log Configuration ===');
  console.log('Global override:', localStorage.getItem('em3-log-all') || '(none)');
  console.log('Module levels:');
  Object.keys(DEFAULT_LEVELS).forEach(module => {
    if (module === '_default') return;
    const override = localStorage.getItem(`em3-log-${module}`);
    const effective = getLogLevel(module);
    console.log(`  ${module}: ${effective}${override ? ' (overridden)' : ''}`);
  });
}

// Expose utilities to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).em3Log = {
    setLevel: setLogLevel,
    setAll: setAllLogLevels,
    reset: resetLogLevels,
    show: showLogConfig,
  };
}
